/**
 * One store: what correction memory has learned there, and a way to forget a lesson.
 *
 * A lesson is learned when a receipt is confirmed, and applies to every later receipt from the
 * store. Forgetting one means the next receipt reads that line afresh; lines already named keep
 * their product.
 */

import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { ChevronLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Notice } from "@/components/ui/Field";
import { rememberedQuery, storesQuery, useForget, type StoreProduct } from "./queries";

export function StorePage() {
  const { t } = useTranslation();
  const { storeId } = useParams({ from: "/app/settings/stores/$storeId" });
  const store = useQuery({
    ...storesQuery,
    select: (stores) => stores.find((candidate) => candidate.id === storeId),
  });
  const remembered = useQuery(rememberedQuery(storeId));

  return (
    <section className="space-y-5">
      <header className="space-y-2">
        <Link
          to="/settings/stores"
          className="-ml-1 inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-800"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden />
          {t("catalog.stores.title")}
        </Link>
        <h1 className="text-xl font-semibold">{store.data?.name ?? ""}</h1>
        <p className="text-sm text-stone-600">{t("catalog.remembered.subtitle")}</p>
      </header>

      {remembered.isPending && <p className="text-stone-500">{t("common.loading")}</p>}
      {remembered.isError && <Notice tone="error">{t("settings.errors.loadFailed")}</Notice>}
      {remembered.data?.length === 0 && (
        <Notice tone="info">{t("catalog.remembered.empty")}</Notice>
      )}

      {remembered.data && remembered.data.length > 0 && (
        <ul className="divide-y divide-stone-200 overflow-hidden rounded-lg border border-stone-200 bg-white">
          {remembered.data.map((lesson) => (
            <Lesson key={lesson.id} lesson={lesson} />
          ))}
        </ul>
      )}
    </section>
  );
}

function Lesson({ lesson }: { lesson: StoreProduct }) {
  const { t } = useTranslation();
  const forget = useForget();

  return (
    <li className="flex items-center gap-3 px-4 py-3">
      <div className="min-w-0 flex-1">
        <div className="font-medium text-stone-900">{lesson.product.name}</div>
        {/* The key it is matched by: what the receipt prints, without price or spaces. */}
        <div className="truncate font-mono text-xs text-stone-500">
          {lesson.store_code ? `${lesson.store_code} · ${lesson.raw_text}` : lesson.raw_text}
        </div>
      </div>
      <button
        type="button"
        disabled={forget.isPending}
        className="text-sm text-stone-500 underline"
        aria-label={t("catalog.remembered.forgetOf", { name: lesson.product.name })}
        onClick={() => forget.mutate(lesson.id)}
      >
        {t("catalog.remembered.forget")}
      </button>
    </li>
  );
}
